import React, { useState, useEffect } from "react";
import { SEARCH } from "../../../core/theme/searchTheme";
import { lookupPostcode, suggestPostcodes, normalisePostcode } from "../../../core/api/postcodesApi";
import { RADIUS_MILES } from "../../../core/locations/radius";

const SUGGEST_DELAY_MS = 250;
// Radius slider stops: none, then the miles.
const RADIUS_STOPS = [null, ...RADIUS_MILES];
const QUARTER_NAMES = { N: "north", E: "east", S: "south", W: "west" };

// Business locations (the Your business postcode, plus other sites), the
// radius around them, the postcodes searched, and extra postcodes.
// The radius picks come from core/locations/radius.js (in useScanPlan); each
// is a removable tag, and removing one doesn't replace it.
export default function LocationsSection({ form, plan }) {
  const main = plan.centres.find(c => c.isMain);
  const step = Math.max(0, RADIUS_STOPS.indexOf(form.radius));

  return (
    <div>
      <div className={SEARCH.sectionHeader}>
        <span className={SEARCH.sectionTitle}>Locations</span>
        <span className={SEARCH.sectionHint}>{plan.postcodes.length} of {plan.maxPostcodes} postcodes</span>
      </div>

      <div className={SEARCH.subLabel}>Business locations</div>
      <div className="space-y-1.5">
        {main ? (
          <span className={SEARCH.postcodeTagBusiness} title="Change it in Your business">
            {main.postcode}<span className={SEARCH.postcodeTagNote}>your business</span>
          </span>
        ) : (
          <p className={SEARCH.note}>Add your business postcode above, or add a location.</p>
        )}
        {form.sites.map(site => <SiteInput key={site.key} site={site} form={form} />)}
        <button className={SEARCH.linkButton} onClick={form.addSite}>+ Add another location</button>
      </div>

      <div className={`${SEARCH.subLabel} mt-3 flex items-baseline justify-between`}>
        <span>Radius</span>
        <span className="text-slate-500 font-normal">{form.radius ? `${form.radius} miles` : "None — just each location's own district"}</span>
      </div>
      <input
        type="range" min={0} max={RADIUS_STOPS.length - 1} step={1} value={step}
        onChange={e => form.setRadius(RADIUS_STOPS[Number(e.target.value)])}
        className={SEARCH.slider} aria-label="Radius in miles"
      />
      <div className={SEARCH.sliderTicks}>
        {RADIUS_STOPS.map(r => (
          <button key={r || 0} onClick={() => form.setRadius(r)} className={r === form.radius ? "text-violet-800 font-semibold" : ""}>{r || "None"}</button>
        ))}
      </div>

      {plan.postcodes.length > 0 && (
        <>
          <div className={`${SEARCH.subLabel} mt-3 flex items-baseline justify-between`}>
            <span>Postcodes searched</span>
            {form.radius && plan.centres.length > 0 && (
              <button className={SEARCH.linkButton} onClick={form.shufflePicks} title="Pick the next most populated districts, with near and far turned a quarter">Shuffle</button>
            )}
          </div>
          <div className={SEARCH.tagRow}>
            {plan.postcodes.map(p => (
              <span key={p.postcode} className={SEARCH.postcodeTag} title={tagTitle(p)}>
                {p.postcode}
                <button
                  onClick={() => (p.isExtra ? form.removePostcode(p.postcode) : form.removePick(p.postcode))}
                  className={SEARCH.postcodeTagRemove} aria-label={`Remove ${p.postcode}`}
                >×</button>
              </span>
            ))}
          </div>
        </>
      )}
      {plan.gaps.map(g => (
        <p key={`${g.centre}-${g.quarter}`} className={SEARCH.note}>
          No postcode district to the {QUARTER_NAMES[g.quarter]} of {g.centre} within {form.radius} miles — skipped.
        </p>
      ))}

      <div className={`${SEARCH.subLabel} mt-3`}>Extra postcodes</div>
      <ExtraPostcodeInput form={form} searched={plan.postcodes} />
    </div>
  );
}

// "Rotherham · east of S1 2AB, mid-distance"
function tagTitle(p) {
  const place = (p.places || []).join(", ");
  if (p.isExtra) return [place, "added by you"].filter(Boolean).join(" · ");
  if (p.isCentre) return [place, `the district of ${p.centre}`].filter(Boolean).join(" · ");
  const where = `${QUARTER_NAMES[p.quarter]} of ${p.centre}, ${p.band === "mid" ? "mid-distance" : p.band}`;
  return [place, where].filter(Boolean).join(" · ");
}

// Another business location (multi-site, franchises): its own postcode box,
// looked up when you leave it.
function SiteInput({ site, form }) {
  const [error, setError] = useState("");

  const check = async () => {
    const text = site.text.trim();
    if (!text) { setError(""); return; }
    const place = await lookupPostcode(text);
    if (!place) {
      setError(`"${normalisePostcode(text)}" isn't a UK postcode we can find.`);
      return;
    }
    setError("");
    form.updateSite(site.key, { text: place.postcode, place });
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          className={`${SEARCH.inputSmall} !w-36`}
          placeholder="Postcode"
          value={site.text}
          autoFocus={!site.text}
          onChange={e => { form.updateSite(site.key, { text: e.target.value, place: null }); setError(""); }}
          onBlur={check}
          onKeyDown={e => { if (e.key === "Enter") check(); }}
        />
        <button onClick={() => form.removeSite(site.key)} className={SEARCH.tagRemove} aria-label="Remove this location">×</button>
      </div>
      {error && <p className={SEARCH.error}>{error}</p>}
    </div>
  );
}

// Postcode box with suggestions from postcodes.io. Each postcode is looked
// up so its map position and town are stored. Added on top of the radius picks.
function ExtraPostcodeInput({ form, searched }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const text = input.trim();
    if (text.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const list = await suggestPostcodes(text);
      if (!cancelled) setSuggestions(list);
    }, SUGGEST_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [input]);

  const add = async (text) => {
    if (!text.trim()) return;
    setAdding(true);
    const place = await lookupPostcode(text);
    setAdding(false);
    if (!place) {
      setError(`"${text.trim().toUpperCase()}" isn't a UK postcode we can find.`);
      return;
    }
    if (searched.some(p => p.postcode === place.postcode)) {
      setError(`${place.postcode} is already searched.`);
      return;
    }
    form.addPostcode(place);
    setInput("");
    setSuggestions([]);
    setError("");
  };

  return (
    <div>
      <div className="relative">
        <input
          className={SEARCH.input}
          placeholder="e.g. S1 or S1 2AB"
          value={input}
          disabled={adding}
          onChange={e => { setInput(e.target.value); setError(""); }}
          onKeyDown={e => {
            if (e.key === "Enter") { e.preventDefault(); add(suggestions[0] ? suggestions[0].postcode : input); }
            if (e.key === "Escape") setSuggestions([]);
          }}
          onBlur={() => setTimeout(() => setSuggestions([]), 150)}
        />
        {suggestions.length > 0 && (
          <div className={SEARCH.dropdown}>
            {suggestions.map((s, i) => (
              <button key={s.postcode} className={i === 0 ? SEARCH.dropdownItemActive : SEARCH.dropdownItem} onMouseDown={e => e.preventDefault()} onClick={() => add(s.postcode)}>
                {s.postcode}{s.hint && <span className={SEARCH.dropdownHint}>{s.hint}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className={SEARCH.error}>{error}</p>}
    </div>
  );
}
